import axios from "axios";
import { BASE_URL } from "../api"; // Assuming there's a BASE_URL exported from src/api.js
import { getApiHeaders } from "../utils/helpers";

// Fetch all symbols for Add Watchlist
export const getAllSymbols = async (accountNumber) => {
    try {
        const response = await axios.post(`${BASE_URL}/MT5M/get_all_symbols?login=${accountNumber}`, { group: null }, { headers: getApiHeaders() });
        return response.data;
    } catch (error) {
        console.error("Error fetching all symbols", error);
        throw error;
    }
};

// Fetch watchlist items
export const getWatchlist = async (accountNumber) => {
    try {
        const res = await axios.get(`${BASE_URL}/MT5M/get_favorite_symbole?account=${accountNumber}`, { headers: getApiHeaders() });
        // The endpoint might return { code: 200, data: [...] }
        return res.data;
    } catch (error) {
        console.error("Error fetching watchlist", error);
        throw error;
    }
};

export const addFavoriteSymbol = async (symbolData, accountNumber) => {
    try {
        const symbol = typeof symbolData === 'string' ? symbolData : (symbolData.symbol || symbolData.feedSymbol || "");
        
        // Strict payload matching FastAPI Pydantic: AddFavoriteSymbolRequest
        const payload = {
            account: String(accountNumber || symbolData?.account || ""),
            symbol: String(symbol),
            description: String(symbolData?.description || symbolData?.name || symbol),
            path: String(symbolData?.path || "Forex"),
            digits: parseInt(symbolData?.digits !== undefined && symbolData?.digits !== null ? symbolData.digits : (symbol.includes("JPY") ? 3 : symbol.includes("XAU") ? 2 : 5), 10),
            contract_size: parseFloat(symbolData?.contract_size !== undefined && symbolData?.contract_size !== null ? symbolData.contract_size : 100000.0),
            volume_min: parseFloat(symbolData?.volume_min !== undefined && symbolData?.volume_min !== null ? symbolData.volume_min : 0.01),
            volume_max: parseFloat(symbolData?.volume_max !== undefined && symbolData?.volume_max !== null ? symbolData.volume_max : 100.0),
            volume_step: parseFloat(symbolData?.volume_step !== undefined && symbolData?.volume_step !== null ? symbolData.volume_step : 0.01),
            currency_base: String(symbolData?.currency_base || (symbol.length >= 6 ? symbol.slice(0, 3) : "EUR")),
            currency_profit: String(symbolData?.currency_profit || (symbol.length >= 6 ? symbol.slice(3, 6) : "USD")),
            currency_margin: String(symbolData?.currency_margin || "USD")
        };

        const response = await axios.post(`${BASE_URL}/MT5M/add_favorite_symbol`, payload, { headers: getApiHeaders() });
        return response.data;
    } catch (error) {
        console.error("Error adding favorite symbol", error);
        throw error;
    }
};

export const removeFavoriteSymbol = async (id) => {
    try {
        const response = await axios.delete(`${BASE_URL}/MT5M/remove_favorite_symbol`, {
            data: { id },
            headers: getApiHeaders()
        });
        return response.data;
    } catch (error) {
        console.error("Error removing favorite symbol", error);
        throw error;
    }
};

// Place an order
export const placeOrder = async (orderData) => {
    try {
        const { account_number, symbol, volume, order_type, target_price, sl, tp } = orderData;
        const isPending = target_price !== undefined && target_price !== null;
        const endpoint = isPending ? `${BASE_URL}/MT5M/open_pending_order_dll` : `${BASE_URL}/MT5M/open_trade_dll`;
        
        const params = { account_number, symbol, volume, order_type };
        if (isPending) {
            params.price = target_price;
        }
        params.sl = sl ? sl : null;
        params.tp = tp ? tp : null;

        const res = await axios.post(endpoint, null, {
            params: params,
            headers: getApiHeaders()
        });
        return res.data;
    } catch (error) {
        console.error("Error placing order", error);
        throw error;
    }
};

// Close an order/position
export const closePositionTrade = async (accountNumber, ticket) => {
    try {
        const res = await axios.post(`${BASE_URL}/MT5M/close_trade_dll`, null, {
            params: { account_number: accountNumber, ticket: ticket },
            headers: getApiHeaders()
        });
        return res.data;
    } catch (error) {
        console.error("Error closing trade", error);
        throw error;
    }
};

// Fetch account details (Margin, Equity, Balance, etc.)
export const getAccountDetails = async (accountNumber) => {
    try {
        const response = await axios.post(`${BASE_URL}/MT5M/get_free_margin_equity?account_number=${accountNumber}`, {}, { headers: getApiHeaders() });
        return response.data;
    } catch (error) {
        console.error("Error fetching account details", error);
        throw error;
    }
};

// --- DUMMY APIs for future implementation ---

export const getLivePositions = async (accountNumber) => {
    try {
        const response = await axios.post(`${BASE_URL}/MT5M/my_trade_data`, null, {
            params: { loginid: accountNumber, trade_type: 'live' },
            headers: getApiHeaders()
        });

        if (response.data && response.data.data) {
            response.data.data = response.data.data.map(item => ({
                id: item.Position,
                symbol: item.Symbol,
                assetClass: item.Symbol.includes('BTC') || item.Symbol.includes('ETH') ? 'CRYPTO' : 'FOREX',
                type: item.Action === 0 ? 'BUY' : 'SELL',
                lot: item.Volume,
                openPrice: item.PriceOpen,
                currentPrice: item.PriceCurrent,
                sl: item.PriceSL || item.sl || item.SL || 0,
                tp: item.PriceTP || item.tp || item.TP || 0,
                profit: item.Profit,
                openTime: item.TimeCreate
            }));
        }

        return response.data;
    } catch (error) {
        console.error("Error fetching live positions", error);
        throw error;
    }
};

export const getPendingOrders = async () => {
    try {
        const response = await axios.get(`${BASE_URL}/MT5M/pending_tread_history`, {
            headers: getApiHeaders()
        });

        if (response.data && response.data.data) {
            response.data.data = response.data.data.map(item => ({
                id: item.ticket,
                symbol: item.symbol || "-",
                type: (item.order_type && item.order_type.toLowerCase().includes('buy')) ? 'BUY' : 'SELL',
                lot: item.volume,
                targetPrice: item.price || 0,
                bid: item.bid || 0,
                ask: item.ask || 0,
                sl: item.sl || item.SL || item.PriceSL || 0,
                tp: item.tp || item.TP || item.PriceTP || 0,
                currentPrice: (item.order_type && item.order_type.toLowerCase().includes('sell')) ? (item.bid || 0) : (item.ask || 0),
                createdTime: item.ondate || "-"
            }));
        }

        return response.data;
    } catch (error) {
        console.error("Error fetching pending orders", error);
        throw error;
    }
};

export const getOrderHistory = async (accountNumber) => {
    try {
        // const response = await axios.post(`${BASE_URL}/MT5M/my_trade_data`, null, {
        //     params: { loginid: accountNumber, trade_type: 'history' },
        //     headers: getApiHeaders()
        // });

        const response = await axios.get(`${BASE_URL}/MT5M/tread_history`, {
            headers: getApiHeaders()
        });

        if (response.data && response.data.data) {
            let trades = response.data.data;
            if (accountNumber) {
                trades = trades.filter(item => String(item.account_number) === String(accountNumber));
            }

            response.data.data = trades.map(item => ({
                id: item.position_id || item.ticket,
                accountNumber: item.account_number,
                symbol: item.symbol || "-",
                assetClass: item.symbol && (item.symbol.includes('BTC') || item.symbol.includes('ETH')) ? 'CRYPTO' : 'FOREX',
                type: item.action === 0 || item.action === '0' || item.action === 'Buy' || item.action === 'BUY' ? 'BUY' : 'SELL',
                lot: item.volume,
                openPrice: item.price || 0,
                closePrice: item.price || 0,
                profit: item.profit,
                closeTime: item.time ? new Date(item.time).toLocaleString() : "-"
            }));
        }

        return response.data;
    } catch (error) {
        console.error("Error fetching order history", error);
        throw error;
    }
};
